import { useState, useEffect, useCallback, useMemo } from 'react'
import {
	Box,
	Typography,
	useMediaQuery,
	useTheme,
	CircularProgress,
	Popover,
	Button,
	Dialog,
	DialogContent,
	IconButton
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { Icon as Iconify } from '@iconify/react'
import { PieChart } from '@mui/x-charts/PieChart'
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts'
import { DayPicker } from 'react-day-picker'
import 'react-day-picker/dist/style.css'
import { supabase } from '../utils/supabase'
import { getCurrencySymbol } from '../utils/currencyUtils'
import {
	getTotalRevenue,
	getAppointmentsCount,
	getAverageTicketValue,
	getTotalPetsServed,
	getTotalTips,
	getTotalTaxes,
	getNetSales,
	getServicesSummary,
	getTopServices,
	getPetsSummary,
	getTopPetBreeds,
	getClientsPerformance,
	getTodaysAppointments,
	resolveRange,
	getComparisonRange,
	computeChange
} from '../utils/reports'
import AppointmentDetailsDialog from './AppointmentDetailsDialog'
import PetAvatar from './PetAvatar'
import MobileDashboardAppointmentCard from './MobileDashboardAppointmentCard'

const QUICK_DATE_OPTIONS = [
	{ value: 'today', label: 'Today' },
	{ value: 'this_week', label: 'This Week' },
	{ value: 'last_week', label: 'Last Week' },
	{ value: 'this_month', label: 'This Month' },
	{ value: 'last_month', label: 'Last Month' },
	{ value: 'this_year', label: 'This Year' },
	{ value: 'last_year', label: 'Last Year' }
]

const ALL_COMPARE_OPTIONS = [
	{ value: 'prior_period', label: 'Prior Period' },
	{ value: 'prior_day', label: 'Prior Day' },
	{ value: 'prior_week', label: 'Prior Week' },
	{ value: 'prior_month', label: 'Prior Month' },
	{ value: 'prior_year', label: 'Prior Year' }
]

function getCompareConfig(dateKey) {
	if (!dateKey) return { defaultCompare: 'prior_period', available: ['prior_period'] }
	switch (dateKey) {
		case 'today':
			return { defaultCompare: 'prior_day', available: ['prior_day', 'prior_week', 'prior_month', 'prior_year'] }
		case 'this_week':
		case 'last_week':
			return { defaultCompare: 'prior_week', available: ['prior_week', 'prior_month', 'prior_year'] }
		case 'this_month':
		case 'last_month':
			return { defaultCompare: 'prior_month', available: ['prior_month', 'prior_year'] }
		case 'this_year':
		case 'last_year':
			return { defaultCompare: 'prior_year', available: ['prior_year'] }
		default:
			return { defaultCompare: 'prior_period', available: ['prior_period'] }
	}
}

const INVOICE_OPTIONS = [
	{ value: 'all', label: 'All' },
	{ value: 'completed', label: 'Closed' }
]

function getQuickDateRange(key, includeFuture = false) {
	const now = new Date()
	const y = now.getFullYear()
	const m = now.getMonth()
	const d = now.getDate()
	const pad = (n) => String(n).padStart(2, '0')
	const fmt = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`
	const today = fmt(now)

	const dayOfWeek = now.getDay()
	const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1

	switch (key) {
		case 'today':
			return { from: today, to: today }
		case 'this_week': {
			const mon = new Date(y, m, d - mondayOffset)
			const sun = new Date(y, m, d - mondayOffset + 6)
			return { from: fmt(mon), to: includeFuture ? fmt(sun) : today }
		}
		case 'last_week': {
			const prevMon = new Date(y, m, d - mondayOffset - 7)
			const prevSun = new Date(y, m, d - mondayOffset - 1)
			return { from: fmt(prevMon), to: fmt(prevSun) }
		}
		case 'this_month': {
			const monthEnd = new Date(y, m + 1, 0)
			return { from: `${y}-${pad(m + 1)}-01`, to: includeFuture ? fmt(monthEnd) : today }
		}
		case 'last_month': {
			const firstPrev = new Date(y, m - 1, 1)
			const lastPrev = new Date(y, m, 0)
			return { from: fmt(firstPrev), to: fmt(lastPrev) }
		}
		case 'this_year':
			return { from: `${y}-01-01`, to: includeFuture ? `${y}-12-31` : today }
		case 'last_year':
			return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` }
		default:
			return { from: today, to: today }
	}
}

const BAR_COLORS = ['#CB00F9', '#EE00FF', '#FF20E9', '#FF82EC', '#FF81D5']

const SERVICES_CHART_COLORS = { services: '#EB6CFF', addOns: '#7F00BA' }
const PETS_CHART_COLORS = { dog: '#ECE861', cat: '#FF7E33', fallback: ['#63B3ED', '#68D391', '#FC8181', '#D69E2E', '#9F7AEA'] }
const BREED_BAR_COLORS = ['#FF6B00', '#FF8A0D', '#FFAB1A', '#FFC626', '#FFE033']
const CLIENT_CHART_COLORS = { returning: '#00ECE4', new: '#35A484' }

function FilterButton({ label, value, onClick, joinLeft, joinRight }) {
	return (
		<Box
			onClick={onClick}
			sx={{
				display: 'flex',
				alignItems: 'center',
				gap: '10px',
				p: '8px 16px',
				bgcolor: '#FFFFFF',
				border: '1px solid #CBD5E0',
				borderRadius: joinLeft ? '0px 6px 6px 0px' : joinRight ? '6px 0px 0px 6px' : '6px',
				borderLeft: joinLeft ? 'none' : '1px solid #CBD5E0',
				cursor: 'pointer',
				'&:hover': { bgcolor: '#F7FAFC' }
			}}
		>
			<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '1.21em', letterSpacing: '-0.018em', color: 'rgba(0,0,0,0.65)' }}>
				{label}
			</Typography>
			<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '14px', lineHeight: '1.21em', color: '#000000' }}>
				{value}
			</Typography>
		</Box>
	)
}

function FilterDropdown({ anchorEl, open, onClose, options, selected, onSelect }) {
	return (
		<Popover
			open={open}
			anchorEl={anchorEl}
			onClose={onClose}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
			transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			slotProps={{
				paper: {
					sx: { borderRadius: '12px', boxShadow: '0px 16px 32px -8px rgba(12,12,13,0.4)', p: '12px', mt: '4px' }
				}
			}}
		>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '160px' }}>
				{options.map(opt => (
					<Box
						key={opt.value}
						onClick={() => { onSelect(opt.value); onClose() }}
						sx={{
							p: '10px 8px',
							borderRadius: '7px',
							cursor: 'pointer',
							bgcolor: selected === opt.value ? 'rgba(0,0,0,0.1)' : 'transparent',
							'&:hover': { bgcolor: selected === opt.value ? 'rgba(0,0,0,0.12)' : 'rgba(0,0,0,0.04)' }
						}}
					>
						<Typography sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 500,
							fontSize: '14px',
							lineHeight: '1.21em',
							color: '#000000'
						}}>
							{opt.label}
						</Typography>
					</Box>
				))}
			</Box>
		</Popover>
	)
}

function DateFilterDropdown({
	anchorEl,
	open,
	onClose,
	isMobile,
	selectedQuickDate,
	onSelectQuickDate,
	customRange,
	onApplyCustomRange
}) {
	const [fromDate, setFromDate] = useState(customRange?.from || '')
	const [toDate, setToDate] = useState(customRange?.to || '')
	const [pickerRange, setPickerRange] = useState({ from: undefined, to: undefined })
	const [mobilePickerOpen, setMobilePickerOpen] = useState(false)

	const parseYmd = (value) => {
		if (!value) return undefined
		const [yy, mm, dd] = value.split('-').map(Number)
		if (!yy || !mm || !dd) return undefined
		return new Date(yy, mm - 1, dd)
	}

	const formatYmd = (date) => {
		if (!(date instanceof Date) || Number.isNaN(date.getTime())) return ''
		const pad = (n) => String(n).padStart(2, '0')
		return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
	}

	useEffect(() => {
		if (!open) return
		setFromDate(customRange?.from || '')
		setToDate(customRange?.to || '')
		setPickerRange({
			from: parseYmd(customRange?.from),
			to: parseYmd(customRange?.to)
		})
	}, [open, customRange?.from, customRange?.to])

	const canApply = Boolean(fromDate && toDate && fromDate <= toDate)

	return (
		<>
			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={onClose}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				slotProps={{
					paper: {
						sx: {
							borderRadius: '12px',
							boxShadow: '0px 16px 32px -8px rgba(12,12,13,0.4)',
							p: '12px',
							mt: '4px'
						}
					}
				}}
			>
				<Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '12px' }}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '170px' }}>
						{QUICK_DATE_OPTIONS.map(opt => (
							<Box
								key={opt.value}
								onClick={() => { onSelectQuickDate(opt.value); onClose() }}
								sx={{
									p: '10px 8px',
									borderRadius: '7px',
									cursor: 'pointer',
									bgcolor: selectedQuickDate === opt.value ? 'rgba(0,0,0,0.1)' : 'transparent',
									'&:hover': { bgcolor: selectedQuickDate === opt.value ? 'rgba(0,0,0,0.12)' : 'rgba(0,0,0,0.04)' }
								}}
							>
								<Typography sx={{
									fontFamily: 'Inter, sans-serif',
									fontWeight: 500,
									fontSize: '14px',
									lineHeight: '1.21em',
									color: '#000000'
								}}>
									{opt.label}
								</Typography>
							</Box>
						))}
						{/* Mobile: custom range entry */}
						{isMobile && (
							<Box
								onClick={() => { setMobilePickerOpen(true); onClose() }}
								sx={{
									p: '10px 8px',
									borderRadius: '7px',
									cursor: 'pointer',
									bgcolor: selectedQuickDate === 'custom' ? 'rgba(0,0,0,0.1)' : 'transparent',
									'&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
								}}
							>
								<Typography sx={{
									fontFamily: 'Inter, sans-serif',
									fontWeight: 500,
									fontSize: '14px',
									lineHeight: '1.21em',
									color: '#5B4FF2'
								}}>
									Custom range…
								</Typography>
							</Box>
						)}
					</Box>

					{!isMobile && (
						<Box sx={{ width: '1px', bgcolor: 'rgba(0,0,0,0.08)', alignSelf: 'stretch' }} />
					)}

					{!isMobile && (
						<Box sx={{ minWidth: '280px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
							<Typography sx={{
								fontFamily: 'Inter, sans-serif',
								fontWeight: 600,
								fontSize: '13px',
								lineHeight: '1.21em',
								color: '#4A5568'
							}}>
								Custom range
							</Typography>
							<Box sx={{
								border: '1px solid #E2E8F0',
								borderRadius: '10px',
								p: '8px',
								'& .rdp': {
									'--rdp-accent-color': '#5B4FF2',
									'--rdp-background-color': '#EEF2FF',
									margin: 0,
									fontFamily: 'Inter, sans-serif',
									fontSize: '13px'
								},
								'& .rdp-months': { gap: '10px' },
								'& .rdp-month': { margin: 0 },
								'& .rdp-day_button': {
									borderRadius: '8px',
									width: '34px',
									height: '34px'
								}
							}}>
								<DayPicker
									mode="range"
									resetOnSelect
									numberOfMonths={1}
									pagedNavigation
									showOutsideDays
									weekStartsOn={1}
									selected={pickerRange}
									onSelect={(range) => {
										const next = range || { from: undefined, to: undefined }
										// Swap detection: only `from` was set, DayPicker swapped because
										// user clicked an earlier date — next.from shifted away from original.
										if (
											pickerRange.from && !pickerRange.to &&
											next.from && next.to &&
											formatYmd(next.from) !== formatYmd(pickerRange.from)
										) {
											const reset = { from: next.from, to: undefined }
											setPickerRange(reset)
											setFromDate(formatYmd(reset.from))
											setToDate('')
											return
										}
										setPickerRange(next)
										setFromDate(formatYmd(next.from))
										setToDate(formatYmd(next.to))
									}}
								/>
							</Box>
							<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
								<Button
									variant="contained"
									size="small"
									disabled={!canApply}
									onClick={() => {
										if (!canApply) return
										onApplyCustomRange({ from: fromDate, to: toDate })
										onClose()
									}}
									sx={{
										textTransform: 'none',
										bgcolor: '#5B4FF2',
										'&:hover': { bgcolor: '#4E43D8' }
									}}
								>
									Apply
								</Button>
							</Box>
						</Box>
					)}
				</Box>
			</Popover>

			{/* Mobile custom range bottom sheet */}
			<Dialog
				open={mobilePickerOpen}
				onClose={() => setMobilePickerOpen(false)}
				fullWidth
				PaperProps={{
					sx: {
						position: 'fixed',
						bottom: 0,
						left: 0,
						right: 0,
						m: 0,
						borderRadius: '16px 16px 0 0',
						maxHeight: '85vh',
						width: '100%',
						maxWidth: '100%'
					}
				}}
			>
				<DialogContent sx={{ p: '16px 20px 32px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
					{/* Header */}
					<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
						<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '16px', color: '#1A202C' }}>
							Custom range
						</Typography>
						<IconButton size="small" onClick={() => setMobilePickerOpen(false)}>
							<CloseIcon fontSize="small" />
						</IconButton>
					</Box>

					{/* Selected range summary */}
					<Box sx={{ display: 'flex', gap: '8px' }}>
						{['From', 'To'].map((label, idx) => {
							const val = idx === 0 ? fromDate : toDate
							return (
								<Box key={label} sx={{
									flex: 1,
									bgcolor: '#F7FAFC',
									border: '1px solid #E2E8F0',
									borderRadius: '8px',
									p: '8px 12px'
								}}>
									<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '11px', color: '#718096', fontWeight: 500 }}>
										{label}
									</Typography>
									<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: val ? '#1A202C' : '#A0AEC0', fontWeight: 500, mt: '2px' }}>
										{val || '—'}
									</Typography>
								</Box>
							)
						})}
					</Box>

					{/* Calendar */}
					<Box sx={{
						'& .rdp': {
							'--rdp-accent-color': '#5B4FF2',
							'--rdp-background-color': '#EEF2FF',
							margin: '0 auto',
							fontFamily: 'Inter, sans-serif',
							fontSize: '13px',
							width: '100%'
						},
						'& .rdp-months': { justifyContent: 'center' },
						'& .rdp-month': { width: '100%' },
						'& .rdp-table': { width: '100%' },
						'& .rdp-day_button': { borderRadius: '8px', width: '38px', height: '38px' }
					}}>
						<DayPicker
							mode="range"
							resetOnSelect
							numberOfMonths={1}
							pagedNavigation
							showOutsideDays
							weekStartsOn={1}
							selected={pickerRange}
							onSelect={(range) => {
								const next = range || { from: undefined, to: undefined }
								if (
									pickerRange.from && !pickerRange.to &&
									next.from && next.to &&
									formatYmd(next.from) !== formatYmd(pickerRange.from)
								) {
									const reset = { from: next.from, to: undefined }
									setPickerRange(reset)
									setFromDate(formatYmd(reset.from))
									setToDate('')
									return
								}
								setPickerRange(next)
								setFromDate(formatYmd(next.from))
								setToDate(formatYmd(next.to))
							}}
						/>
					</Box>

					{/* Apply button */}
					<Button
						variant="contained"
						fullWidth
						disabled={!canApply}
						onClick={() => {
							if (!canApply) return
							onApplyCustomRange({ from: fromDate, to: toDate })
							setMobilePickerOpen(false)
						}}
						sx={{
							textTransform: 'none',
							borderRadius: '10px',
							py: '12px',
							bgcolor: '#5B4FF2',
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: '15px',
							'&:hover': { bgcolor: '#4E43D8' },
							'&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#A0AEC0' }
						}}
					>
						Apply
					</Button>
				</DialogContent>
			</Dialog>
		</>
	)
}

function FilterBar({
	dateKey,
	dateLabel,
	onDateChange,
	compareWith,
	onCompareChange,
	invoiceStatus,
	onInvoiceChange,
	staffId,
	onStaffChange,
	employees,
	isMobile,
	customRange,
	onCustomRangeApply
}) {
	const [dateAnchor, setDateAnchor] = useState(null)
	const [vsAnchor, setVsAnchor] = useState(null)
	const [invoiceAnchor, setInvoiceAnchor] = useState(null)
	const [staffAnchor, setStaffAnchor] = useState(null)

	const compareOptions = useMemo(() => {
		const { available } = getCompareConfig(dateKey)
		return ALL_COMPARE_OPTIONS.filter(o => available.includes(o.value))
	}, [dateKey])
	const vsLabel = compareOptions.find(o => o.value === compareWith)?.label
		|| ALL_COMPARE_OPTIONS.find(o => o.value === compareWith)?.label
		|| compareWith
	const invoiceLabel = INVOICE_OPTIONS.find(o => o.value === invoiceStatus)?.label || invoiceStatus
	const staffLabel = staffId === 'all' ? 'All' : employees.find(e => e.id === staffId)?.first_name || 'All'

	const staffOptions = [
		{ value: 'all', label: 'All' },
		...employees.map(e => ({ value: e.id, label: `${e.first_name} ${e.last_name}` }))
	]

	return (
		<Box sx={{
			display: 'flex',
			alignItems: 'center',
			gap: '10px',
			flexWrap: 'wrap'
		}}>
			<Box sx={{ display: 'flex', alignItems: 'center' }}>
				<FilterButton label="Date" value={dateLabel} onClick={(e) => setDateAnchor(e.currentTarget)} joinRight />
				<FilterButton label="vs" value={vsLabel} onClick={(e) => setVsAnchor(e.currentTarget)} joinLeft />
			</Box>
			<FilterButton label="Invoices" value={invoiceLabel} onClick={(e) => setInvoiceAnchor(e.currentTarget)} />
			<FilterButton label="Staff" value={staffLabel} onClick={(e) => setStaffAnchor(e.currentTarget)} />

			<DateFilterDropdown
				anchorEl={dateAnchor}
				open={Boolean(dateAnchor)}
				onClose={() => setDateAnchor(null)}
				isMobile={isMobile}
				selectedQuickDate={dateKey}
				onSelectQuickDate={onDateChange}
				customRange={customRange}
				onApplyCustomRange={onCustomRangeApply}
			/>
			<FilterDropdown anchorEl={vsAnchor} open={Boolean(vsAnchor)} onClose={() => setVsAnchor(null)} options={compareOptions} selected={compareWith} onSelect={onCompareChange} />
			<FilterDropdown anchorEl={invoiceAnchor} open={Boolean(invoiceAnchor)} onClose={() => setInvoiceAnchor(null)} options={INVOICE_OPTIONS} selected={invoiceStatus} onSelect={onInvoiceChange} />
			<FilterDropdown anchorEl={staffAnchor} open={Boolean(staffAnchor)} onClose={() => setStaffAnchor(null)} options={staffOptions} selected={staffId} onSelect={onStaffChange} />
		</Box>
	)
}

function ChangeIndicator({ change }) {
	if (change == null) return null
	const isPositive = change >= 0
	return (
		<Typography sx={{
			fontFamily: 'Inter, sans-serif',
			fontWeight: 600,
			fontSize: '12px',
			lineHeight: '1.21em',
			width: '65px',
			textAlign: 'right',
			color: isPositive ? '#00AD59' : '#E53E3E'
		}}>
			{isPositive ? '+' : ''}{change}%
		</Typography>
	)
}

function ReportCard({ title, value, change, vsLabel, icon, loading, children, isMobile }) {
	return (
		<Box sx={{
			bgcolor: '#FFFFFF',
			borderRadius: '12px',
			border: '1px solid rgba(0, 0, 0, 0.2)',
			p: isMobile ? '20px' : '24px',
			display: 'flex',
			flexDirection: 'column',
			gap: '16px',
			minHeight: '140px'
		}}>
			<Box sx={{
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				gap: '12px'
			}}>
				<Typography sx={{
					fontFamily: 'Inter, sans-serif',
					fontWeight: 400,
					fontSize: '13px',
					lineHeight: '1.21em',
					color: 'rgba(0,0,0,0.6)',
					flex: 1
				}}>
					{title}
				</Typography>
				{icon && (
					<Box sx={{
						width: 25,
						height: 25,
						borderRadius: '50%',
						bgcolor: '#FEE2FF',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						flexShrink: 0
					}}>
						<Iconify icon={icon} width={13} height={13} color="#BF00FF" />
					</Box>
				)}
				{children}
			</Box>
			{loading ? (
				<CircularProgress size={24} sx={{ color: '#9F7AEA' }} />
			) : (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
					<Typography sx={{
						fontFamily: 'Inter, sans-serif',
						fontWeight: 600,
						fontSize: isMobile ? '20px' : '32px',
						lineHeight: '1.2em',
						color: '#000000'
					}}>
						{value}
					</Typography>
					{change != null && (
						<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', lineHeight: '1.21em', color: change >= 0 ? '#00AD59' : '#E53E3E' }}>
							<Box component="span" sx={{ fontWeight: 600 }}>{change >= 0 ? '+' : ''}{change}%</Box>
							<Box component="span" sx={{ fontWeight: 400, color: 'rgba(0,0,0,0.6)' }}> {vsLabel || 'vs prior period'}</Box>
						</Typography>
					)}
				</Box>
			)}
		</Box>
	)
}


function SalesSummary({ data, loading, isMobile, currencySymbol, vsLabel }) {
	const revenueCents = data?.revenueCents ?? 0
	const tipsCents = data?.tipsCents ?? 0
	const taxesCents = data?.taxesCents ?? 0
	const netCents = revenueCents - tipsCents - taxesCents

	const fmt = (cents) => `${currencySymbol}${(cents / 100).toFixed(2)}`
	const changePct = data?.revenueChange != null
		? `${data.revenueChange >= 0 ? '+' : ''}${data.revenueChange}%`
		: null

	const deductions = [
		{ label: 'Tips Collected', value: fmt(tipsCents), change: data?.tipsChange },
		{ label: 'Taxes Deducted', value: fmt(taxesCents), change: data?.taxesChange }
	]

	return (
		<Box sx={{
			bgcolor: '#FFFFFF',
			borderRadius: '10px',
			border: '1px solid rgba(0, 0, 0, 0.12)',
			p: isMobile ? '20px' : '24px 30px',
			display: 'flex',
			flexDirection: 'column',
			gap: '30px'
		}}>
			<Typography sx={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '19px', lineHeight: '1.36em', color: '#000000' }}>
				Sales Summary
			</Typography>

			{loading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
					<CircularProgress size={28} sx={{ color: '#9F7AEA' }} />
				</Box>
			) : (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '50px' }}>
					{/* Total collected revenue — hero section */}
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
						<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '13px', lineHeight: '1.21em', color: 'rgba(0,0,0,0.8)' }}>
							Total collected revenue
						</Typography>
						<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '22px', lineHeight: '1.21em', color: '#000000' }}>
							{fmt(revenueCents)}
						</Typography>
						{changePct && (
							<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '12px', lineHeight: '1.21em', color: data.revenueChange >= 0 ? '#00AD59' : '#E53E3E' }}>
								<Box component="span" sx={{ fontWeight: 600 }}>{changePct}</Box>
								<Box component="span" sx={{ fontWeight: 400, color: 'rgba(0,0,0,0.6)' }}> {vsLabel || 'vs prior period'}</Box>
							</Typography>
						)}
					</Box>

					{/* Deductions + Net Sales */}
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
						{deductions.map((row) => (
							<Box key={row.label} sx={{ display: 'flex', alignItems: 'center', gap: '5px', justifyContent: 'space-between' }}>
								<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '13px', lineHeight: '1.21em', color: 'rgba(0,0,0,0.8)' }}>
									{row.label}
								</Typography>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
									<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '1.21em', color: '#000000' }}>
										{row.value}
									</Typography>
									<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
										<ChangeIndicator change={row.change} />
									</Box>
								</Box>
							</Box>
						))}

						{/* Divider */}
						<Box sx={{ height: '1px', bgcolor: '#CBD5E0' }} />

						{/* Net Sales */}
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', justifyContent: 'space-between' }}>
							<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '13px', lineHeight: '1.21em', color: 'rgba(0,0,0,0.8)' }}>
								Net Sales
							</Typography>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
								<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '14px', lineHeight: '1.21em', color: '#000000' }}>
									{fmt(netCents)}
								</Typography>
								<Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
									<ChangeIndicator change={data?.netChange} />
								</Box>
							</Box>
						</Box>
					</Box>
				</Box>
			)}
		</Box>
	)
}

const NET_SALES_GROUP_OPTIONS = [
	{ value: 'day', label: 'Daily' },
	{ value: 'week', label: 'Weekly' },
	{ value: 'month', label: 'Monthly' }
]

function NetSalesChart({ data, loading, isMobile, groupBy, onGroupByChange, currencySymbol }) {
	const chartData = useMemo(() => {
		if (!data?.closed || !data?.expected) return []
		return data.expected.map((entry, i) => ({
			period: entry.period,
			expected: entry.totalCents / 100,
			closed: (data.closed[i]?.totalCents ?? 0) / 100
		}))
	}, [data])

	const formatYAxis = (val) => {
		if (val >= 1000) return `${currencySymbol}${(val / 1000).toFixed(val % 1000 === 0 ? 0 : 1)}k`
		return `${currencySymbol}${val}`
	}

	const CustomTooltip = ({ active, payload, label }) => {
		if (!active || !payload?.length) return null
		return (
			<Box sx={{
				bgcolor: '#FFFFFF',
				borderRadius: '10px',
				border: '0.5px solid #FFC2F8',
				boxShadow: '0px 4px 7px rgba(3, 2, 41, 0.07)',
				px: '23px',
				py: '8px',
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				gap: '2px'
			}}>
				<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '12px', color: '#000000', opacity: 0.8 }}>
					{label}
				</Typography>
				{payload.map((p, i) => (
					<Typography key={i} sx={{ fontFamily: 'Nunito, sans-serif', fontWeight: 600, fontSize: '14px', color: p.color }}>
						{p.dataKey === 'expected' ? 'Expected' : 'Collected'}: {currencySymbol}{p.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
					</Typography>
				))}
			</Box>
		)
	}

	return (
		<Box sx={{
			bgcolor: '#FFFFFF',
			borderRadius: '12px',
			border: '1px solid rgba(0, 0, 0, 0.12)',
			p: isMobile ? '14px' : '14px 20px',
			display: 'flex',
			flexDirection: 'column',
			gap: '10px',
			minHeight: 0
		}}>
			<Box sx={{ p: '10px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
					<Typography sx={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '19px', lineHeight: '1.36em', color: '#000000' }}>
						Net sales
					</Typography>
					<Box sx={{ display: 'flex', bgcolor: 'rgba(0,0,0,0.06)', borderRadius: '6px', p: '4px' }}>
						{NET_SALES_GROUP_OPTIONS.map(tab => (
							<Box
								key={tab.value}
								onClick={() => onGroupByChange(tab.value)}
								sx={{
									px: '10px',
									width: '70px',
									py: '4px',
									borderRadius: '6px',
									cursor: 'pointer',
									bgcolor: groupBy === tab.value ? '#FFFFFF' : 'transparent',
									transition: 'all 0.15s ease'
								}}
							>
								<Typography sx={{
									fontFamily: 'Nunito, sans-serif',
									fontWeight: groupBy === tab.value ? 600 : 400,
									fontSize: '12px',
									lineHeight: '1.36em',
									textAlign: 'center',
									color: groupBy === tab.value ? '#000000' : '#809FB8'
								}}>
									{tab.label}
								</Typography>
							</Box>
						))}
					</Box>
				</Box>

				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
						<CircularProgress size={28} sx={{ color: '#9F7AEA' }} />
					</Box>
				) : chartData.length === 0 ? (
					<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#718096', textAlign: 'center', py: 4 }}>
						No data available
					</Typography>
				) : (
					<Box sx={{ width: '100%', height: 340 }}>
						<ResponsiveContainer width="100%" height="100%">
							<AreaChart data={chartData} margin={{ top: 10, right: 30, left: -10, bottom: 0 }}>
								<defs>
									<linearGradient id="gradExpected" x1="0" y1="0" x2="1" y2="0">
										<stop offset="0%" stopColor="#5BC4FF" stopOpacity={0.06} />
										<stop offset="100%" stopColor="#FD5DEF" stopOpacity={0.06} />
									</linearGradient>
									<linearGradient id="strokeExpected" x1="0" y1="0" x2="1" y2="0">
										<stop offset="0%" stopColor="#5BC4FF" />
										<stop offset="100%" stopColor="#FF5BEF" />
									</linearGradient>
									<linearGradient id="gradClosed" x1="0" y1="0" x2="0" y2="1">
										<stop offset="0%" stopColor="#38B2AC" stopOpacity={0.08} />
										<stop offset="100%" stopColor="#38B2AC" stopOpacity={0} />
									</linearGradient>
								</defs>
								<CartesianGrid strokeDasharray="3 3" stroke="rgba(3,2,41,0.06)" vertical={false} />
								<XAxis
									dataKey="period"
									tick={{ fontFamily: 'Nunito, sans-serif', fontSize: 12, fill: 'rgba(0,0,0,0.8)' }}
									tickLine={false}
									axisLine={false}
									dy={8}
									opacity={0.7}
								/>
								<YAxis
									tickFormatter={formatYAxis}
									tick={{ fontFamily: 'Nunito, sans-serif', fontSize: 12, fill: 'rgba(0,0,0,0.8)' }}
									tickLine={false}
									axisLine={false}
									width={50}
									opacity={0.7}
								/>
								<Tooltip content={<CustomTooltip />} cursor={{ stroke: '#605BFF', strokeWidth: 1, strokeDasharray: '2 7' }} />
								<Area
									type="monotone"
									dataKey="expected"
									name="Expected Sales"
									stroke="url(#strokeExpected)"
									strokeWidth={3}
									fill="url(#gradExpected)"
									dot={false}
									activeDot={{ r: 5, fill: '#FF5BEF', stroke: '#FFFFFF', strokeWidth: 2 }}
								/>
								<Area
									type="monotone"
									dataKey="closed"
									name="Closed Sales"
									stroke="#38B2AC"
									strokeWidth={2}
									fill="url(#gradClosed)"
									dot={false}
									activeDot={{ r: 5, fill: '#38B2AC', stroke: '#FFFFFF', strokeWidth: 2 }}
								/>
							</AreaChart>
						</ResponsiveContainer>
					</Box>
				)}
			</Box>
		</Box>
	)
}

const SERVICES_TAB_OPTIONS = [
	{ value: 'services', label: 'Services' },
	{ value: 'addons', label: 'Add-ons' },
	{ value: 'categories', label: 'Categories' }
]

function ServicesSummaryChart({ data, loading, isMobile, merchant, dateRange, priorRange, invoiceStatus, empFilter, currencySymbol, mobileView }) {
	const cur = data?.current
	const pri = data?.prior
	const serviceCount = cur?.serviceCount ?? 0
	const addOnCount = cur?.addOnCount ?? 0
	const total = serviceCount + addOnCount
	const servicePct = total > 0 ? ((serviceCount / total) * 100).toFixed(1) : '0.0'
	const addOnPct = total > 0 ? ((addOnCount / total) * 100).toFixed(1) : '0.0'

	const [activeTab, setActiveTab] = useState('services')
	const [tabData, setTabData] = useState([])
	const [tabPriorMap, setTabPriorMap] = useState({})
	const [loadingTab, setLoadingTab] = useState(false)

	useEffect(() => {
		if (!merchant?.id) return
		let cancelled = false
		const load = async () => {
			setLoadingTab(true)
			try {
				const opts = { groupBy: activeTab, sortBy: 'revenue', limit: 5, status: invoiceStatus, employeeId: empFilter }
				const [cur, pri] = await Promise.all([
					getTopServices(merchant.id, dateRange, opts),
					getTopServices(merchant.id, priorRange, opts)
				])
				if (!cancelled) {
					setTabData(cur)
					const map = {}
					for (const item of pri) { map[item.name] = item.revenueCents }
					setTabPriorMap(map)
				}
			} catch (e) { console.error('Tab services fetch error:', e) }
			finally { if (!cancelled) setLoadingTab(false) }
		}
		load()
		return () => { cancelled = true }
	}, [merchant?.id, dateRange, priorRange, invoiceStatus, empFilter, activeTab])

	const pieData = [
		{ id: 0, value: serviceCount, color: SERVICES_CHART_COLORS.services },
		{ id: 1, value: addOnCount, color: SERVICES_CHART_COLORS.addOns }
	]

	const maxRevenue = tabData.length > 0
		? Math.max(...tabData.map(s => s.revenueCents))
		: 1

	const donutContent = (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
			<Typography sx={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '18px', lineHeight: '1.36em', color: '#000000' }}>
				Services Summary
			</Typography>
			{loading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
					<CircularProgress size={28} sx={{ color: '#9F7AEA' }} />
				</Box>
			) : (
				<Box sx={{ display: 'flex', flexDirection: mobileView ? 'column' : 'row', alignItems: 'center', gap: mobileView ? '20px' : '30px', px: mobileView ? 0 : '30px', justifyContent: 'center' }}>
					<Box sx={{ position: 'relative', width: mobileView ? 140 : 180, height: mobileView ? 140 : 180, flexShrink: 0 }}>
						<PieChart
							series={[{
								data: pieData,
								innerRadius: mobileView ? 42 : 55,
								outerRadius: mobileView ? 68 : 88,
								paddingAngle: 2,
								cornerRadius: 4,
								arcLabel: () => null
							}]}
							width={mobileView ? 140 : 180}
							height={mobileView ? 140 : 180}
							tooltip={{ trigger: 'none' }}
							legend={{ hidden: true }}
							slotProps={{ legend: { hidden: true } }}
							margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
						/>
						<Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
							<Typography sx={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: mobileView ? '20px' : '26px', lineHeight: '1.36em', color: '#030229' }}>
								{total}
							</Typography>
							<Typography sx={{ fontFamily: 'Nunito, sans-serif', fontWeight: 400, fontSize: mobileView ? '10px' : '12px', lineHeight: '1.36em', color: '#030229', opacity: 0.7 }}>
								services provided
							</Typography>
						</Box>
					</Box>
					<Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: mobileView ? '14px' : '28px' }}>
						<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: isMobile ? '10px' : '0px' }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: mobileView ? 'auto' : '110px' }}>
								<Box sx={{ width: 12, height: 12, bgcolor: SERVICES_CHART_COLORS.services, borderRadius: '4px', flexShrink: 0 }} />
								<Typography sx={{ fontFamily: 'Nunito, sans-serif', fontWeight: 600, fontSize: '13px', color: '#030229', opacity: 0.7 }}>Services</Typography>
							</Box>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
								<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '13px', color: '#000000', minWidth: '80px' }}>{serviceCount} ({servicePct}%)</Typography>
								<ChangeIndicator change={computeChange(serviceCount, pri?.serviceCount ?? 0)} />
							</Box>
						</Box>
						<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: isMobile ? '10px' : '0px' }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: mobileView ? 'auto' : '110px' }}>
								<Box sx={{ width: 12, height: 12, bgcolor: SERVICES_CHART_COLORS.addOns, borderRadius: '4px', flexShrink: 0 }} />
								<Typography sx={{ fontFamily: 'Nunito, sans-serif', fontWeight: 600, fontSize: '13px', color: '#030229', opacity: 0.7 }}>Add-ons</Typography>
							</Box>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
								<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '13px', color: '#000000', minWidth: '80px' }}>{addOnCount} ({addOnPct}%)</Typography>
								<ChangeIndicator change={computeChange(addOnCount, pri?.addOnCount ?? 0)} />
							</Box>
						</Box>
					</Box>
				</Box>
			)}
		</Box>
	)

	const listContent = (
		<Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '16px', flexWrap: 'wrap', gap: '8px' }}>
				<Typography sx={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '18px', lineHeight: '1.36em', color: '#000000' }}>
					Top {activeTab === 'addons' ? 'Add-ons' : activeTab === 'categories' ? 'Categories' : 'Services'}
				</Typography>
				<Box sx={{ display: 'flex', bgcolor: '#F4F4F4', borderRadius: '8px', p: '2px' }}>
					{SERVICES_TAB_OPTIONS.map(tab => (
						<Box key={tab.value} onClick={() => setActiveTab(tab.value)} sx={{ px: '12px', py: '6px', borderRadius: '6px', cursor: 'pointer', bgcolor: activeTab === tab.value ? '#FFFFFF' : 'transparent', boxShadow: activeTab === tab.value ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s ease' }}>
							<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: activeTab === tab.value ? 600 : 400, fontSize: '12px', lineHeight: '1.21em', color: activeTab === tab.value ? '#000000' : '#718096' }}>{tab.label}</Typography>
						</Box>
					))}
				</Box>
			</Box>
			{loadingTab ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 6, flex: 1 }}><CircularProgress size={28} sx={{ color: '#9F7AEA' }} /></Box>
			) : tabData.length === 0 ? (
				<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#718096', textAlign: 'center', py: 4 }}>No data available</Typography>
			) : (
				<Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', flex: 1, gap: isMobile ? '10px' : '0px' }}>
					{tabData.map((item, index) => {
						const barPct = maxRevenue > 0 ? (item.revenueCents / maxRevenue) * 100 : 0
						const priorCents = tabPriorMap[item.name] ?? 0
						const change = computeChange(item.revenueCents, priorCents)
						return (
							<Box key={item.name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', height: '20%' }}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
									<Typography title={item.name} sx={{ fontFamily: 'Nunito, Inter, sans-serif', fontWeight: 500, fontSize: '14px', lineHeight: '1.21em', color: 'rgba(0, 0, 0, 0.90);', width: '100px', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</Typography>
									<Box sx={{ flex: 1, height: '6px', bgcolor: 'transparent', borderRadius: '3px', overflow: 'hidden', minWidth: '30px' }}>
										<Box sx={{ width: `${barPct}%`, height: '100%', bgcolor: BAR_COLORS[index % BAR_COLORS.length], borderRadius: '3px', transition: 'width 0.4s ease' }} />
									</Box>
								</Box>
								<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px', flexShrink: 0, width: '160px' }}>
									<Typography sx={{ fontFamily: 'Nunito, Inter, sans-serif', fontWeight: 500, fontSize: '14px', lineHeight: '1.21em', color: 'rgba(0, 0, 0, 0.90);', textAlign: 'right' }}>{currencySymbol}{(item.revenueCents / 100).toFixed(2)}</Typography>
									<ChangeIndicator change={change} />
								</Box>
							</Box>
						)
					})}
				</Box>
			)}
		</Box>
	)

	if (mobileView === 'donut') {
		return <Box sx={{ bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.2)', p: '16px' }}>{donutContent}</Box>
	}
	if (mobileView === 'list') {
		return <Box sx={{ bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.2)', p: '16px' }}>{listContent}</Box>
	}

	return (
		<Box sx={{
			bgcolor: '#FFFFFF',
			borderRadius: '10px',
			border: '1px solid rgba(0, 0, 0, 0.2)',
			p: '26px',
			display: 'flex',
			flexDirection: 'row',
			gap: '30px'
		}}>
			<Box sx={{ width: '55%', flexShrink: 0 }}>{donutContent}</Box>
			{listContent}
		</Box>
	)
}

function getPetSpeciesColor(name, index) {
	const lower = name.toLowerCase()
	if (lower === 'dog') return PETS_CHART_COLORS.dog
	if (lower === 'cat') return PETS_CHART_COLORS.cat
	return PETS_CHART_COLORS.fallback[index % PETS_CHART_COLORS.fallback.length]
}

const PETS_TAB_OPTIONS = [
	{ value: 'breeds', label: 'Breeds' },
	{ value: 'species', label: 'Species' }
]

function PetsSummaryChart({ data, loading, isMobile, merchant, dateRange, priorRange, invoiceStatus, empFilter, currencySymbol, mobileView }) {
	const cur = data?.current
	const pri = data?.prior
	const speciesCounts = useMemo(() => cur?.speciesCounts ?? [], [cur])
	const totalPets = cur?.totalPets ?? 0

	const [activeTab, setActiveTab] = useState('breeds')
	const [tabData, setTabData] = useState([])
	const [tabPriorMap, setTabPriorMap] = useState({})
	const [loadingTab, setLoadingTab] = useState(false)

	useEffect(() => {
		if (!merchant?.id) return
		let cancelled = false
		const load = async () => {
			setLoadingTab(true)
			try {
				if (activeTab === 'breeds') {
					const [curBreeds, priBreeds] = await Promise.all([
						getTopPetBreeds(merchant.id, dateRange, { sortBy: 'revenue', limit: 5, status: invoiceStatus, employeeId: empFilter }),
						getTopPetBreeds(merchant.id, priorRange, { sortBy: 'revenue', limit: 5, status: invoiceStatus, employeeId: empFilter })
					])
					if (!cancelled) {
						setTabData(curBreeds)
						const map = {}
						for (const item of priBreeds) { map[item.name] = item.revenueCents }
						setTabPriorMap(map)
					}
				} else {
					const speciesItems = speciesCounts.map(s => ({
						name: s.name,
						revenueCents: 0,
						revenueFormatted: '',
						volume: s.count
					}))
					const priorMap = {}
					for (const s of (pri?.speciesCounts ?? [])) { priorMap[s.name] = s.count }
					if (!cancelled) {
						setTabData(speciesItems)
						setTabPriorMap(priorMap)
					}
				}
			} catch (e) { console.error('Pets tab fetch error:', e) }
			finally { if (!cancelled) setLoadingTab(false) }
		}
		load()
		return () => { cancelled = true }
	}, [merchant?.id, dateRange, priorRange, invoiceStatus, empFilter, activeTab, speciesCounts, pri])

	const pieData = speciesCounts.map((s, i) => ({
		id: i,
		value: s.count,
		color: getPetSpeciesColor(s.name, i)
	}))

	const priSpeciesMap = useMemo(() => {
		const map = {}
		for (const s of (pri?.speciesCounts ?? [])) { map[s.name] = s.count }
		return map
	}, [pri])

	const isBreedTab = activeTab === 'breeds'
	const maxValue = tabData.length > 0
		? Math.max(...tabData.map(s => isBreedTab ? s.revenueCents : s.volume))
		: 1

	const donutContent = (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
			<Typography sx={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '18px', lineHeight: '1.36em', color: '#000000' }}>
				Pets Summary
			</Typography>
			{loading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} sx={{ color: '#9F7AEA' }} /></Box>
			) : (
				<Box sx={{ display: 'flex', flexDirection: mobileView ? 'column' : 'row', alignItems: 'center', gap: mobileView ? '20px' : '30px', px: mobileView ? 0 : '30px', justifyContent: 'center' }}>
					<Box sx={{ position: 'relative', width: mobileView ? 140 : 180, height: mobileView ? 140 : 180, flexShrink: 0 }}>
						<PieChart
							series={[{ data: pieData, innerRadius: mobileView ? 42 : 55, outerRadius: mobileView ? 68 : 88, paddingAngle: 2, cornerRadius: 4, arcLabel: () => null }]}
							width={mobileView ? 140 : 180} height={mobileView ? 140 : 180}
							tooltip={{ trigger: 'none' }} legend={{ hidden: true }} slotProps={{ legend: { hidden: true } }}
							margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
						/>
						<Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
							<Typography sx={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: mobileView ? '20px' : '26px', lineHeight: '1.36em', color: '#030229' }}>{totalPets}</Typography>
							<Typography sx={{ fontFamily: 'Nunito, sans-serif', fontWeight: 400, fontSize: mobileView ? '10px' : '12px', lineHeight: '1.36em', color: '#030229', opacity: 0.7 }}>pets served</Typography>
						</Box>
					</Box>
					<Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: mobileView ? '14px' : '20px' }}>
						{speciesCounts.map((s, i) => {
							const pct = totalPets > 0 ? ((s.count / totalPets) * 100).toFixed(1) : '0.0'
							return (
								<Box key={s.name} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: isMobile ? '10px' : '0px' }}>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: mobileView ? 'auto' : '110px' }}>
										<Box sx={{ width: 12, height: 12, bgcolor: getPetSpeciesColor(s.name, i), borderRadius: '4px', flexShrink: 0 }} />
										<Typography sx={{ fontFamily: 'Nunito, sans-serif', fontWeight: 600, fontSize: '13px', color: '#030229', opacity: 0.7 }}>{s.name}</Typography>
									</Box>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: mobileView ? 'auto' : '110px' }}>
										<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '13px', color: '#000000', minWidth: '80px' }}>{s.count} ({pct}%)</Typography>
										<ChangeIndicator change={computeChange(s.count, priSpeciesMap[s.name] ?? 0)} />
									</Box>
								</Box>
							)
						})}
					</Box>
				</Box>
			)}
		</Box>
	)

	const listContent = (
		<Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '16px', flexWrap: 'wrap', gap: '8px' }}>
				<Typography sx={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '18px', lineHeight: '1.36em', color: '#000000' }}>Top {activeTab === 'species' ? 'Species' : 'Breeds'}</Typography>
				<Box sx={{ display: 'flex', bgcolor: '#F4F4F4', borderRadius: '8px', p: '2px' }}>
					{PETS_TAB_OPTIONS.map(tab => (
						<Box key={tab.value} onClick={() => setActiveTab(tab.value)} sx={{ px: '12px', py: '6px', borderRadius: '6px', cursor: 'pointer', bgcolor: activeTab === tab.value ? '#FFFFFF' : 'transparent', boxShadow: activeTab === tab.value ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s ease' }}>
							<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: activeTab === tab.value ? 600 : 400, fontSize: '12px', lineHeight: '1.21em', color: activeTab === tab.value ? '#000000' : '#718096' }}>{tab.label}</Typography>
						</Box>
					))}
				</Box>
			</Box>
			{loadingTab ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 6, flex: 1 }}><CircularProgress size={28} sx={{ color: '#9F7AEA' }} /></Box>
			) : tabData.length === 0 ? (
				<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#718096', textAlign: 'center', py: 4 }}>No data available</Typography>
			) : (
				<Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', flex: 1, gap: isMobile ? '10px' : '0px' }}>
					{tabData.map((item, index) => {
						const rawVal = isBreedTab ? item.revenueCents : item.volume
						const barPct = maxValue > 0 ? (rawVal / maxValue) * 100 : 0
						const priorVal = tabPriorMap[item.name] ?? 0
						const change = computeChange(rawVal, priorVal)
						return (
							<Box key={item.name} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', height: '20%' }}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
									<Typography title={item.name} sx={{ fontFamily: 'Nunito, Inter, sans-serif', fontWeight: 500, fontSize: '14px', color: 'rgba(0, 0, 0, 0.90);', width: '100px', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</Typography>
									<Box sx={{ flex: 1, height: '6px', bgcolor: 'transparent', borderRadius: '3px', overflow: 'hidden', minWidth: '30px' }}>
										<Box sx={{ width: `${barPct}%`, height: '100%', bgcolor: BREED_BAR_COLORS[index % BREED_BAR_COLORS.length], borderRadius: '3px', transition: 'width 0.4s ease' }} />
									</Box>
								</Box>
								<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '6px', flexShrink: 0, width: '160px' }}>
									<Typography sx={{ fontFamily: 'Nunito, Inter, sans-serif', fontWeight: 500, fontSize: '14px', color: 'rgba(0, 0, 0, 0.90);', textAlign: 'right' }}>{isBreedTab ? `${currencySymbol}${(item.revenueCents / 100).toFixed(2)}` : item.volume}</Typography>
									<ChangeIndicator change={change} />
								</Box>
							</Box>
						)
					})}
				</Box>
			)}
		</Box>
	)

	if (mobileView === 'donut') {
		return <Box sx={{ bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.2)', p: '16px' }}>{donutContent}</Box>
	}
	if (mobileView === 'list') {
		return <Box sx={{ bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.2)', p: '16px' }}>{listContent}</Box>
	}

	return (
		<Box sx={{ bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.2)', p: '26px', display: 'flex', flexDirection: 'row', gap: '30px' }}>
			<Box sx={{ width: '55%', flexShrink: 0 }}>{donutContent}</Box>
			{listContent}
		</Box>
	)
}

const STATUS_STYLES = {
	booked: { bg: '#B2F5EA', border: '#4FD1C5', color: '#2C7A7B', label: 'Upcoming' },
	bookPending: { bg: '#FEFCBF', border: '#ECC94B', color: '#975A16', label: 'Pending' },
	completed: { bg: '#C6F6D5', border: '#68D391', color: '#276749', label: 'Completed' },
	noShow: { bg: '#FED7D7', border: '#FC8181', color: '#9B2C2C', label: 'No Show' },
	cancelled: { bg: '#FED7D7', border: '#FC8181', color: '#9B2C2C', label: 'Cancelled' }
}

function TodaysAppointments({ data, loading, isMobile, merchant, onEditAppointment }) {
	const [sortKey, setSortKey] = useState('startTs')
	const [sortDir, setSortDir] = useState('asc')

	const handleSort = (key) => {
		if (sortKey === key) {
			setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')
		} else {
			setSortKey(key)
			setSortDir('asc')
		}
	}

	const sorted = useMemo(() => {
		if (!data?.length) return []
		return [...data].sort((a, b) => {
			const aVal = a[sortKey] ?? ''
			const bVal = b[sortKey] ?? ''
			const cmp = typeof aVal === 'string' ? aVal.localeCompare(bVal) : aVal - bVal
			return sortDir === 'asc' ? cmp : -cmp
		})
	}, [data, sortKey, sortDir])

	const SortIcon = ({ active, dir }) => (
		<Box component="span" sx={{ display: 'inline-flex', ml: '4px', opacity: active ? 1 : 0.3, fontSize: '10px', verticalAlign: 'middle' }}>
			{active && dir === 'desc' ? '▼' : '▲'}
		</Box>
	)

	const columns = [
		{ key: 'clientName', label: 'Client name', sortable: true, flex: 1 },
		{ key: 'petName', label: 'Pet name', sortable: false, flex: 1 },
		{ key: 'status', label: 'Status', sortable: true, flex: 1 },
		{ key: 'serviceName', label: 'Service', sortable: false, flex: 1 },
		{ key: 'startTs', label: 'Date & Time', sortable: true, flex: 1 },
		{ key: 'actions', label: 'Actions', sortable: false, width: '100px' }
	]

	const formatDateTime = (ts) => {
		if (!ts) return '—'
		const d = new Date(ts)
		const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
		const h = d.getHours()
		const m = d.getMinutes()
		const ampm = h >= 12 ? 'PM' : 'AM'
		const h12 = h % 12 || 12
		return `${months[d.getMonth()]} ${d.getDate()}, ${h12}:${String(m).padStart(2, '0')} ${ampm}`
	}

	return (
		<Box sx={{
			bgcolor: '#FFFFFF',
			borderRadius: '12px',
			border: '1px solid rgba(0, 0, 0, 0.12)',
			p: isMobile ? '20px' : '30px 20px',
			display: 'flex',
			flexDirection: 'column',
			gap: '10px'
		}}>
			<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '18px', lineHeight: '1.21em', color: '#000000' }}>
				Today's Appointments
			</Typography>

			{loading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
					<CircularProgress size={28} sx={{ color: '#9F7AEA' }} />
				</Box>
			) : isMobile ? (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
					{sorted.length === 0 ? (
						<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#718096', textAlign: 'center', py: 4 }}>
							No appointments today
						</Typography>
					) : sorted.map(row => (
						<MobileDashboardAppointmentCard
							key={row.id}
							appointment={row}
							onClick={() => onEditAppointment(row)}
						/>
					))}
				</Box>
			) : (
				<Box sx={{ borderRadius: '12px', overflow: 'hidden' }}>
					{/* Header */}
					<Box sx={{ display: 'flex' }}>
						{columns.map(col => (
							<Box
								key={col.key}
								onClick={col.sortable ? () => handleSort(col.key) : undefined}
								sx={{
									flex: col.flex || undefined,
									width: col.width || undefined,
									p: '16px 12px',
									borderBottom: '1px solid #CBD5E0',
									cursor: col.sortable ? 'pointer' : 'default',
									userSelect: 'none',
									display: 'flex',
									alignItems: 'center',
									justifyContent: col.key === 'actions' ? 'center' : 'flex-start',
									gap: '4px'
								}}
							>
								<Typography sx={{
									fontFamily: 'Inter, sans-serif',
									fontWeight: 600,
									fontSize: '14px',
									lineHeight: '1.21em',
									letterSpacing: '-0.018em',
									color: '#000000'
								}}>
									{col.label}
								</Typography>
								{col.sortable && <SortIcon active={sortKey === col.key} dir={sortDir} />}
							</Box>
						))}
					</Box>

					{/* Rows */}
					{sorted.length === 0 ? (
						<Box sx={{ p: '24px', textAlign: 'center' }}>
							<Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#718096' }}>
								No appointments today
							</Typography>
						</Box>
					) : sorted.map(row => {
						const st = STATUS_STYLES[row.status] || STATUS_STYLES.booked
						return (
							<Box key={row.id} sx={{ display: 'flex', '&:not(:last-child)': { borderBottom: '1px solid #CBD5E0' } }}>
								<Box sx={{ flex: 1, p: '16px 12px', display: 'flex', alignItems: 'center' }}>
									<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', letterSpacing: '-0.018em', color: '#000000' }}>
										{row.clientName}
									</Typography>
								</Box>
								<Box sx={{ flex: 1, p: '16px 12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
									<PetAvatar petId={row.petId} size={40} />
									<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', letterSpacing: '-0.018em', color: '#000000' }}>
										{row.petName}
									</Typography>
								</Box>
								<Box sx={{ flex: 1, p: '16px 12px', display: 'flex', alignItems: 'center' }}>
									<Box sx={{
										px: '12px',
										py: '4px',
										borderRadius: '9999px',
										bgcolor: st.bg,
										border: `1px solid ${st.border}`
									}}>
										<Typography sx={{
											fontFamily: 'Inter, sans-serif',
											fontWeight: 400,
											fontSize: '14px',
											lineHeight: '1.71em',
											letterSpacing: '0.036em',
											color: st.color
										}}>
											{st.label}
										</Typography>
									</Box>
								</Box>
								<Box sx={{ flex: 1, p: '16px 12px', display: 'flex', alignItems: 'center' }}>
									<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', letterSpacing: '-0.018em', color: '#000000' }}>
										{row.serviceName}
									</Typography>
								</Box>
								<Box sx={{ flex: 1, p: '16px 12px', display: 'flex', alignItems: 'center' }}>
									<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 400, fontSize: '14px', letterSpacing: '-0.018em', color: '#000000' }}>
										{formatDateTime(row.startTs)}
									</Typography>
								</Box>
								<Box sx={{ width: '100px', p: '16px 12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
									<Box
										onClick={() => onEditAppointment(row)}
										sx={{ cursor: 'pointer', p: '4px', borderRadius: '4px', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
									>
										<svg width="16" height="16" viewBox="0 0 16 16" fill="none">
											<path d="M11.333 1.333a1.886 1.886 0 0 1 2.667 2.667l-8.667 8.667L2 14l1.333-3.333 8-8.667z" stroke="#000000" strokeWidth="1.33" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
									</Box>
								</Box>
							</Box>
						)
					})}
				</Box>
			)}
		</Box>
	)
}

const CLIENT_PERF_STATS = [
	{ key: 'rebookRate', label: 'Average rebook rate', suffix: '%' },
	{ key: 'averageRevenuePerClientFormatted', label: 'Average revenue per client', compareKey: 'averageRevenuePerClientCents', isCurrency: true },
	{ key: 'lateCancellationRate', label: 'Late cancellation rate', suffix: '%' },
	{ key: 'noShowRate', label: 'No-show rate', suffix: '%' },
	{ key: 'averageTipRate', label: 'Average tip rate', suffix: '%' }
]

function ClientAnalytics({ data, loading, isMobile, currencySymbol, mobileView }) {
	const cur = data?.current
	const pri = data?.prior
	const clientsServed = cur?.clientsServed ?? 0
	const returning = cur?.returningClients ?? 0
	const newClients = cur?.newClients ?? 0
	const returningPct = clientsServed > 0 ? ((returning / clientsServed) * 100).toFixed(1) : '0.0'
	const newPct = clientsServed > 0 ? ((newClients / clientsServed) * 100).toFixed(1) : '0.0'

	const pieData = [
		{ id: 0, value: returning, color: CLIENT_CHART_COLORS.returning },
		{ id: 1, value: newClients, color: CLIENT_CHART_COLORS.new }
	]

	const donutContent = (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
			<Typography sx={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '18px', lineHeight: '1.36em', color: '#000000' }}>Client Summary</Typography>
			{loading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} sx={{ color: '#9F7AEA' }} /></Box>
			) : (
				<Box sx={{ display: 'flex', flexDirection: mobileView ? 'column' : 'row', alignItems: 'center', gap: mobileView ? '20px' : '30px', px: mobileView ? 0 : '30px', justifyContent: 'center' }}>
					<Box sx={{ position: 'relative', width: mobileView ? 140 : 180, height: mobileView ? 140 : 180, flexShrink: 0 }}>
						<PieChart
							series={[{ data: pieData, innerRadius: mobileView ? 42 : 55, outerRadius: mobileView ? 68 : 88, paddingAngle: 2, cornerRadius: 4, arcLabel: () => null }]}
							width={mobileView ? 140 : 180} height={mobileView ? 140 : 180}
							tooltip={{ trigger: 'none' }} legend={{ hidden: true }} slotProps={{ legend: { hidden: true } }}
							margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
						/>
						<Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
							<Typography sx={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: mobileView ? '20px' : '26px', lineHeight: '1.36em', color: '#030229' }}>{clientsServed}</Typography>
							<Typography sx={{ fontFamily: 'Nunito, sans-serif', fontWeight: 400, fontSize: mobileView ? '10px' : '12px', lineHeight: '1.36em', color: '#030229', opacity: 0.7 }}>clients served</Typography>
						</Box>
					</Box>
					<Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: mobileView ? '14px' : '28px' }}>
						<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: isMobile ? '10px' : '0px' }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: mobileView ? 'auto' : '110px' }}>
								<Box sx={{ width: 12, height: 12, bgcolor: CLIENT_CHART_COLORS.returning, borderRadius: '4px', flexShrink: 0 }} />
								<Typography sx={{ fontFamily: 'Nunito, sans-serif', fontWeight: 600, fontSize: '13px', color: '#030229', opacity: 0.7 }}>Returning</Typography>
							</Box>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
								<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '13px', color: '#000000', minWidth: '80px' }}>{returning} ({returningPct}%)</Typography>
								<ChangeIndicator change={computeChange(returning, pri?.returningClients ?? 0)} />
							</Box>
						</Box>
						<Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: isMobile ? '10px' : '0px' }}>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: mobileView ? 'auto' : '110px' }}>
								<Box sx={{ width: 12, height: 12, bgcolor: CLIENT_CHART_COLORS.new, borderRadius: '4px', flexShrink: 0 }} />
								<Typography sx={{ fontFamily: 'Nunito, sans-serif', fontWeight: 600, fontSize: '13px', color: '#030229', opacity: 0.7 }}>New</Typography>
							</Box>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
								<Typography sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, fontSize: '13px', color: '#000000', minWidth: '80px' }}>{newClients} ({newPct}%)</Typography>
								<ChangeIndicator change={computeChange(newClients, pri?.newClients ?? 0)} />
							</Box>
						</Box>
					</Box>
				</Box>
			)}
		</Box>
	)

	const listContent = (
		<Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
			<Typography sx={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '18px', lineHeight: '1.36em', color: '#000000', mb: '16px' }}>Client Performance</Typography>
			{loading ? (
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 6, flex: 1 }}><CircularProgress size={28} sx={{ color: '#9F7AEA' }} /></Box>
			) : (
				<Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', flex: 1, gap: '12px' }}>
					{CLIENT_PERF_STATS.map((stat, index) => {
						const curVal = cur?.[stat.key]
						const displayValue = curVal != null ? (stat.suffix ? `${curVal}${stat.suffix}` : (stat.isCurrency ? `${currencySymbol}${(cur[stat.compareKey] / 100).toFixed(2)}` : curVal)) : '—'
						const cmpKey = stat.compareKey || stat.key
						const numCur = cur?.[cmpKey]
						const numPri = pri?.[cmpKey]
						const change = (numCur != null && numPri != null) ? computeChange(numCur, numPri) : null
						return (
							<Box key={stat.key}>
								<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
									<Typography sx={{ fontFamily: 'Nunito, Inter, sans-serif', fontWeight: 500, fontSize: '14px', lineHeight: '1.21em', color: 'rgba(0,0,0,0.9)', flex: 1 }}>{stat.label}</Typography>
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
										<Typography sx={{ fontFamily: 'Nunito, Inter, sans-serif', fontWeight: 500, fontSize: '14px', lineHeight: '1.21em', color: 'rgba(0,0,0,0.9)' }}>{displayValue}</Typography>
										<ChangeIndicator change={change} />
									</Box>
								</Box>
								{index < CLIENT_PERF_STATS.length - 1 && <Box sx={{ width: '100%', height: '1px', bgcolor: '#CBD5E0', mt: '12px' }} />}
							</Box>
						)
					})}
				</Box>
			)}
		</Box>
	)

	if (mobileView === 'donut') {
		return <Box sx={{ bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.2)', p: '16px' }}>{donutContent}</Box>
	}
	if (mobileView === 'list') {
		return <Box sx={{ bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.2)', p: '16px' }}>{listContent}</Box>
	}

	return (
		<Box sx={{ bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.2)', p: '26px', display: 'flex', flexDirection: 'row', gap: '30px' }}>
			<Box sx={{ width: '55%', flexShrink: 0 }}>{donutContent}</Box>
			{listContent}
		</Box>
	)
}

export default function Dashboard({ merchant }) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))
	const cs = getCurrencySymbol(merchant?.currency)
	const fmtCurrency = (cents) => `${cs}${(cents / 100).toFixed(2)}`

	const [employees, setEmployees] = useState([])

	// Global filter bar state
	const [dateKey, setDateKey] = useState('this_month')
	const [customDateRange, setCustomDateRange] = useState({ from: '', to: '' })
	const [isCustomDateRange, setIsCustomDateRange] = useState(false)
	const [compareWith, setCompareWith] = useState('prior_month')
	const [invoiceStatus, setInvoiceStatus] = useState('all')
	const [staffId, setStaffId] = useState('all')

	// Auto-select the correct "vs" default when date selection changes
	useEffect(() => {
		const effectiveKey = isCustomDateRange ? null : dateKey
		const { defaultCompare, available } = getCompareConfig(effectiveKey)
		if (!available.includes(compareWith)) {
			setCompareWith(defaultCompare)
		}
	}, [dateKey, isCustomDateRange, compareWith])

	// Derived ranges
	const includeFutureInCurrentPeriod = invoiceStatus === 'all'
	const dateRange = useMemo(
		() => (
			isCustomDateRange && customDateRange.from && customDateRange.to
				? customDateRange
				: getQuickDateRange(dateKey, includeFutureInCurrentPeriod)
		),
		[isCustomDateRange, customDateRange, dateKey, includeFutureInCurrentPeriod]
	)
	const currentResolved = useMemo(() => resolveRange(dateRange), [dateRange])
	const priorRange = useMemo(() => getComparisonRange(currentResolved, compareWith), [currentResolved, compareWith])
	const empFilter = staffId === 'all' ? null : staffId
	const vsLabel = useMemo(
		() => 'vs ' + ALL_COMPARE_OPTIONS.find(o => o.value === compareWith)?.label || 'vs prior period',
		[compareWith]
	)
	const dateLabel = useMemo(() => {
		if (isCustomDateRange && customDateRange.from && customDateRange.to) {
			return `${customDateRange.from} - ${customDateRange.to}`
		}
		return QUICK_DATE_OPTIONS.find(o => o.value === dateKey)?.label || dateKey
	}, [isCustomDateRange, customDateRange, dateKey])

	// Report data: { current, prior, change }
	const [revenue, setRevenue] = useState({})
	const [appointments, setAppointments] = useState({})
	const [petsServed, setPetsServed] = useState({})
	const [avgTicket, setAvgTicket] = useState({})
	const [salesSummary, setSalesSummary] = useState({})
	const [netSalesData, setNetSalesData] = useState(null)
	const [netSalesGroupBy, setNetSalesGroupBy] = useState('day')
	const [servicesSummaryData, setServicesSummaryData] = useState(null)
	const [petsSummaryData, setPetsSummaryData] = useState(null)
	const [todaysAppts, setTodaysAppts] = useState([])
	const [loadingTodaysAppts, setLoadingTodaysAppts] = useState(false)
	const [editingAppt, setEditingAppt] = useState(null)
	const [clientPerfData, setClientPerfData] = useState({})

	// Loading states
	const [loadingRevenue, setLoadingRevenue] = useState(false)
	const [loadingAppointments, setLoadingAppointments] = useState(false)
	const [loadingPetsServed, setLoadingPetsServed] = useState(false)
	const [loadingAvgTicket, setLoadingAvgTicket] = useState(false)
	const [loadingSalesSummary, setLoadingSalesSummary] = useState(false)
	const [loadingNetSales, setLoadingNetSales] = useState(false)
	const [loadingServicesSummary, setLoadingServicesSummary] = useState(false)
	const [loadingPetsSummary, setLoadingPetsSummary] = useState(false)
	const [loadingClientPerf, setLoadingClientPerf] = useState(false)

	useEffect(() => {
		if (!merchant?.id) return
		const load = async () => {
			const { data } = await supabase
				.from('employees')
				.select('id, first_name, last_name')
				.eq('merchant_id', merchant.id)
				.eq('active', true)
				.order('first_name')
			setEmployees(data || [])
		}
		load()
	}, [merchant?.id])

	const fetchRevenue = useCallback(async () => {
		if (!merchant?.id) return
		setLoadingRevenue(true)
		try {
			const [cur, pri] = await Promise.all([
				getTotalRevenue(merchant.id, dateRange, invoiceStatus, empFilter),
				getTotalRevenue(merchant.id, priorRange, invoiceStatus, empFilter)
			])
			setRevenue({ current: cur, prior: pri, change: computeChange(cur.totalCents, pri.totalCents) })
		} catch (e) { console.error('Revenue fetch error:', e) }
		finally { setLoadingRevenue(false) }
	}, [merchant?.id, dateRange, priorRange, invoiceStatus, empFilter])

	const fetchAppointments = useCallback(async () => {
		if (!merchant?.id) return
		setLoadingAppointments(true)
		try {
			const [cur, pri] = await Promise.all([
				getAppointmentsCount(merchant.id, dateRange, invoiceStatus, empFilter),
				getAppointmentsCount(merchant.id, priorRange, invoiceStatus, empFilter)
			])
			setAppointments({ current: cur, prior: pri, change: computeChange(cur.count, pri.count) })
		} catch (e) { console.error('Appointments fetch error:', e) }
		finally { setLoadingAppointments(false) }
	}, [merchant?.id, dateRange, priorRange, invoiceStatus, empFilter])

	const fetchPetsServed = useCallback(async () => {
		if (!merchant?.id) return
		setLoadingPetsServed(true)
		try {
			const [cur, pri] = await Promise.all([
				getTotalPetsServed(merchant.id, dateRange, invoiceStatus, empFilter),
				getTotalPetsServed(merchant.id, priorRange, invoiceStatus, empFilter)
			])
			setPetsServed({ current: cur, prior: pri, change: computeChange(cur.count, pri.count) })
		} catch (e) { console.error('Pets served fetch error:', e) }
		finally { setLoadingPetsServed(false) }
	}, [merchant?.id, dateRange, priorRange, invoiceStatus, empFilter])

	const fetchAvgTicket = useCallback(async () => {
		if (!merchant?.id) return
		setLoadingAvgTicket(true)
		try {
			const [cur, pri] = await Promise.all([
				getAverageTicketValue(merchant.id, dateRange, invoiceStatus, empFilter),
				getAverageTicketValue(merchant.id, priorRange, invoiceStatus, empFilter)
			])
			setAvgTicket({ current: cur, prior: pri, change: computeChange(cur.averageCents, pri.averageCents) })
		} catch (e) { console.error('Avg ticket fetch error:', e) }
		finally { setLoadingAvgTicket(false) }
	}, [merchant?.id, dateRange, priorRange, invoiceStatus, empFilter])

	const fetchSalesSummary = useCallback(async () => {
		if (!merchant?.id) return
		setLoadingSalesSummary(true)
		try {
			const [rev, tips, taxes, priRev, priTips, priTaxes] = await Promise.all([
				getTotalRevenue(merchant.id, dateRange, 'completed', empFilter),
				getTotalTips(merchant.id, dateRange, empFilter),
				getTotalTaxes(merchant.id, dateRange),
				getTotalRevenue(merchant.id, priorRange, 'completed', empFilter),
				getTotalTips(merchant.id, priorRange, empFilter),
				getTotalTaxes(merchant.id, priorRange)
			])
			const netCents = rev.totalCents - tips.totalCents - taxes.totalCents
			const priNetCents = priRev.totalCents - priTips.totalCents - priTaxes.totalCents
			setSalesSummary({
				revenueCents: rev.totalCents,
				tipsCents: tips.totalCents,
				taxesCents: taxes.totalCents,
				revenueChange: computeChange(rev.totalCents, priRev.totalCents),
				tipsChange: computeChange(tips.totalCents, priTips.totalCents),
				taxesChange: computeChange(taxes.totalCents, priTaxes.totalCents),
				netChange: computeChange(netCents, priNetCents)
			})
		} catch (e) { console.error('Sales summary fetch error:', e) }
		finally { setLoadingSalesSummary(false) }
	}, [merchant?.id, dateRange, priorRange, empFilter])

	const fetchNetSales = useCallback(async () => {
		if (!merchant?.id) return
		setLoadingNetSales(true)
		try {
			const result = await getNetSales(merchant.id, dateRange, netSalesGroupBy, empFilter)
			setNetSalesData(result)
		} catch (e) { console.error('Net sales fetch error:', e) }
		finally { setLoadingNetSales(false) }
	}, [merchant?.id, dateRange, netSalesGroupBy, empFilter])

	const fetchServicesSummary = useCallback(async () => {
		if (!merchant?.id) return
		setLoadingServicesSummary(true)
		try {
			const [cur, pri] = await Promise.all([
				getServicesSummary(merchant.id, dateRange, invoiceStatus, empFilter),
				getServicesSummary(merchant.id, priorRange, invoiceStatus, empFilter)
			])
			setServicesSummaryData({ current: cur, prior: pri })
		} catch (e) { console.error('Services summary fetch error:', e) }
		finally { setLoadingServicesSummary(false) }
	}, [merchant?.id, dateRange, priorRange, invoiceStatus, empFilter])

	const fetchPetsSummary = useCallback(async () => {
		if (!merchant?.id) return
		setLoadingPetsSummary(true)
		try {
			const [cur, pri] = await Promise.all([
				getPetsSummary(merchant.id, dateRange, invoiceStatus, empFilter),
				getPetsSummary(merchant.id, priorRange, invoiceStatus, empFilter)
			])
			setPetsSummaryData({ current: cur, prior: pri })
		} catch (e) { console.error('Pets summary fetch error:', e) }
		finally { setLoadingPetsSummary(false) }
	}, [merchant?.id, dateRange, priorRange, invoiceStatus, empFilter])

	const fetchTodaysAppts = useCallback(async () => {
		if (!merchant?.id) return
		setLoadingTodaysAppts(true)
		try {
			const result = await getTodaysAppointments(merchant.id, empFilter)
			setTodaysAppts(result)
		} catch (e) { console.error('Todays appointments fetch error:', e) }
		finally { setLoadingTodaysAppts(false) }
	}, [merchant?.id, empFilter])

	const fetchClientPerf = useCallback(async () => {
		if (!merchant?.id) return
		setLoadingClientPerf(true)
		try {
			const [cur, pri] = await Promise.all([
				getClientsPerformance(merchant.id, dateRange, invoiceStatus, empFilter),
				getClientsPerformance(merchant.id, priorRange, invoiceStatus, empFilter)
			])
			setClientPerfData({ current: cur, prior: pri })
		} catch (e) { console.error('Client performance fetch error:', e) }
		finally { setLoadingClientPerf(false) }
	}, [merchant?.id, dateRange, priorRange, invoiceStatus, empFilter])

	useEffect(() => { fetchRevenue() }, [fetchRevenue])
	useEffect(() => { fetchAppointments() }, [fetchAppointments])
	useEffect(() => { fetchPetsServed() }, [fetchPetsServed])
	useEffect(() => { fetchAvgTicket() }, [fetchAvgTicket])
	useEffect(() => { fetchSalesSummary() }, [fetchSalesSummary])
	useEffect(() => { fetchNetSales() }, [fetchNetSales])
	useEffect(() => { fetchServicesSummary() }, [fetchServicesSummary])
	useEffect(() => { fetchPetsSummary() }, [fetchPetsSummary])
	useEffect(() => { fetchTodaysAppts() }, [fetchTodaysAppts])
	useEffect(() => { fetchClientPerf() }, [fetchClientPerf])

	return (
		<Box sx={{
			height: isMobile ? 'auto' : '100%',
			minHeight: isMobile ? '100%' : undefined,
			display: 'flex',
			flexDirection: 'column',
			bgcolor: '#ffffff',
			gap: isMobile ? 0 : 2,
			padding: isMobile ? '16px' : '20px',
			paddingBottom: isMobile ? 'calc(48px + env(safe-area-inset-bottom, 0px))' : undefined,
			overflow: isMobile ? 'visible' : 'auto'
		}}>
			{/* Title */}
			<Box sx={{ mb: 2, mr: isMobile ? 0 : 2, flexShrink: 0 }}>
				<Typography sx={{
					fontFamily: 'Inter, sans-serif',
					fontWeight: 600,
					fontSize: '28px',
					lineHeight: '1.286em',
					color: '#000000'
				}}>
					Dashboard
				</Typography>
			</Box>

			{/* Filter Bar */}
			<Box sx={{ mr: isMobile ? 0 : 2, mb: 2, flexShrink: 0 }}>
				<FilterBar
					dateKey={isCustomDateRange ? null : dateKey}
					dateLabel={dateLabel}
					onDateChange={(nextKey) => {
						setDateKey(nextKey)
						setIsCustomDateRange(false)
						const { defaultCompare } = getCompareConfig(nextKey)
						setCompareWith(defaultCompare)
					}}
					isMobile={isMobile}
					customRange={customDateRange}
					onCustomRangeApply={(range) => {
						setCustomDateRange(range)
						setIsCustomDateRange(true)
						setCompareWith('prior_period')
					}}
					compareWith={compareWith} onCompareChange={setCompareWith}
					invoiceStatus={invoiceStatus} onInvoiceChange={setInvoiceStatus}
					staffId={staffId} onStaffChange={setStaffId}
					employees={employees}
				/>
			</Box>

			{/* Report Cards Grid */}
			<Box sx={{
				display: 'grid',
				gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
				gap: '16px',
				mr: isMobile ? 0 : 2
			}}>
				<ReportCard title={invoiceStatus === 'all' ? 'Expected Revenue' : 'Collected Revenue'} value={revenue.current ? fmtCurrency(revenue.current.totalCents) : '—'} change={revenue.change} vsLabel={vsLabel} icon="boxicons:dollar-filled" loading={loadingRevenue} isMobile={isMobile} />
				<ReportCard title="Appointments" value={appointments.current?.count?.toString() ?? '—'} change={appointments.change} vsLabel={vsLabel} icon="hugeicons:appointment-02" loading={loadingAppointments} isMobile={isMobile} />
				<ReportCard title="Total Pets Served" value={petsServed.current?.count?.toString() ?? '—'} change={petsServed.change} vsLabel={vsLabel} icon="streamline-plump:pet-paw" loading={loadingPetsServed} isMobile={isMobile} />
				<ReportCard title="Average Value Per Pet" value={avgTicket.current ? fmtCurrency(avgTicket.current.averageCents) : '—'} change={avgTicket.change} vsLabel={vsLabel} icon="boxicons:dollar-filled" loading={loadingAvgTicket} isMobile={isMobile} />
			</Box>

			{/* Sales Summary + Net Sales Chart */}
			<Box sx={{
				display: 'grid',
				gridTemplateColumns: isMobile ? '1fr' : '1fr 2.5fr',
				gap: '16px',
				mr: isMobile ? 0 : 2,
				mt: 2
			}}>
				<SalesSummary data={salesSummary} loading={loadingSalesSummary} isMobile={isMobile} currencySymbol={cs} vsLabel={vsLabel} />
				<NetSalesChart
					data={netSalesData}
					loading={loadingNetSales}
					isMobile={isMobile}
					groupBy={netSalesGroupBy}
					onGroupByChange={setNetSalesGroupBy}
					currencySymbol={cs}
				/>
			</Box>

			{isMobile ? (
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', mt: 2 }}>
					<ServicesSummaryChart data={servicesSummaryData} loading={loadingServicesSummary} isMobile merchant={merchant} dateRange={dateRange} priorRange={priorRange} invoiceStatus={invoiceStatus} empFilter={empFilter} currencySymbol={cs} mobileView="donut" />
					<ServicesSummaryChart data={servicesSummaryData} loading={loadingServicesSummary} isMobile merchant={merchant} dateRange={dateRange} priorRange={priorRange} invoiceStatus={invoiceStatus} empFilter={empFilter} currencySymbol={cs} mobileView="list" />
					<PetsSummaryChart data={petsSummaryData} loading={loadingPetsSummary} isMobile merchant={merchant} dateRange={dateRange} priorRange={priorRange} invoiceStatus={invoiceStatus} empFilter={empFilter} currencySymbol={cs} mobileView="donut" />
					<PetsSummaryChart data={petsSummaryData} loading={loadingPetsSummary} isMobile merchant={merchant} dateRange={dateRange} priorRange={priorRange} invoiceStatus={invoiceStatus} empFilter={empFilter} currencySymbol={cs} mobileView="list" />
					<ClientAnalytics data={clientPerfData} loading={loadingClientPerf} isMobile currencySymbol={cs} mobileView="donut" />
					<ClientAnalytics data={clientPerfData} loading={loadingClientPerf} isMobile currencySymbol={cs} mobileView="list" />
				</Box>
			) : (
				<>
					<Box sx={{ mr: 2, mt: 2 }}>
						<ServicesSummaryChart data={servicesSummaryData} loading={loadingServicesSummary} isMobile={false} merchant={merchant} dateRange={dateRange} priorRange={priorRange} invoiceStatus={invoiceStatus} empFilter={empFilter} currencySymbol={cs} />
					</Box>
					<Box sx={{ mr: 2, mt: 2 }}>
						<PetsSummaryChart data={petsSummaryData} loading={loadingPetsSummary} isMobile={false} merchant={merchant} dateRange={dateRange} priorRange={priorRange} invoiceStatus={invoiceStatus} empFilter={empFilter} currencySymbol={cs} />
					</Box>
					<Box sx={{ mr: 2, mt: 2 }}>
						<ClientAnalytics data={clientPerfData} loading={loadingClientPerf} isMobile={false} currencySymbol={cs} />
					</Box>
				</>
			)}

			{/* Today's Appointments Table */}
			<Box sx={{ mr: isMobile ? 0 : 2, mt: 2 }}>
				<TodaysAppointments
					data={todaysAppts}
					loading={loadingTodaysAppts}
					isMobile={isMobile}
					merchant={merchant}
					onEditAppointment={(row) => setEditingAppt(row)}
				/>
			</Box>

			{/* Appointment Details Dialog */}
			<AppointmentDetailsDialog
				open={Boolean(editingAppt)}
				onClose={() => setEditingAppt(null)}
				appointment={editingAppt?.appointmentData || null}
				merchant={merchant}
				onAppointmentUpdated={fetchTodaysAppts}
			/>
		</Box>
	)
}
